/* ==========================================================================
   OAKLEY PRIZM — Landing editorial (The Lab)

   Portado desde la landing standalone (app.js) y adaptado a Online Store 2.0:
   - Inicializa por raíz (document o una sección) y marca cada elemento con
     data-prizm-init para no duplicar listeners.
   - Escucha shopify:section:load para re-inicializar cuando el personalizador
     vuelve a renderizar una sección prizm-*.
   Comportamientos: hold-to-compare, play/pausa de video, flechas de carrusel
   y reveal-on-scroll.
   ========================================================================== */
(function () {
  "use strict";

  var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !("IntersectionObserver" in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10%", threshold: 0.08 }
    );
    return revealObserver;
  }

  /* --------------------------------------------------- hold-to-compare --- */
  function initHoldCompare(root) {
    root.querySelectorAll("[data-hold-compare]").forEach(function (comparison) {
      if (comparison.dataset.prizmInit) return;
      comparison.dataset.prizmInit = "1";

      var setActive = function (active) {
        comparison.classList.toggle("is-active", active);
        comparison.setAttribute("aria-pressed", String(active));
      };

      comparison.addEventListener("pointerdown", function (event) {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.preventDefault();
        if (comparison.setPointerCapture) comparison.setPointerCapture(event.pointerId);
        setActive(true);
      });

      ["pointerup", "pointercancel", "lostpointercapture"].forEach(function (eventName) {
        comparison.addEventListener(eventName, function () {
          setActive(false);
        });
      });

      comparison.addEventListener("keydown", function (event) {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        setActive(true);
      });

      comparison.addEventListener("keyup", function (event) {
        if (event.key === " " || event.key === "Enter") setActive(false);
      });

      comparison.addEventListener("blur", function () {
        setActive(false);
      });
    });
  }

  /* ------------------------------------------------------- video toggle --- */
  function initVideoToggle(root) {
    root.querySelectorAll("[data-video-toggle]").forEach(function (button) {
      if (button.dataset.prizmInit) return;
      var frame = button.closest(".media-frame");
      var video = frame ? frame.querySelector("video") : null;
      if (!video) return;
      button.dataset.prizmInit = "1";

      var syncButton = function () {
        var isPaused = video.paused;
        button.setAttribute("aria-label", isPaused ? "Reproducir video" : "Pausar video");
        var span = button.querySelector("span");
        if (span) span.textContent = isPaused ? "▶" : "Ⅱ";
      };

      button.addEventListener("click", function () {
        if (video.paused) {
          var playing = video.play();
          if (playing && typeof playing.catch === "function") {
            playing.catch(function () {
              button.setAttribute("aria-label", "El video no pudo reproducirse");
            });
          }
        } else {
          video.pause();
        }
        syncButton();
      });

      video.addEventListener("play", syncButton);
      video.addEventListener("pause", syncButton);

      if (prefersReducedMotion) {
        video.pause();
        syncButton();
      }
    });
  }

  /* ---------------------------------------------------- scroll controls --- */
  function initScrollControls(root) {
    root.querySelectorAll("[data-scroll-target]").forEach(function (button) {
      if (button.dataset.prizmInit) return;
      button.dataset.prizmInit = "1";

      button.addEventListener("click", function () {
        var track = document.getElementById(button.dataset.scrollTarget);
        if (!track) return;
        var direction = button.dataset.direction === "prev" ? -1 : 1;
        var firstCard = track.firstElementChild;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
        var distance = ((firstCard && firstCard.getBoundingClientRect().width) || track.clientWidth * 0.75) + gap;
        track.scrollBy({ left: direction * distance, behavior: prefersReducedMotion ? "auto" : "smooth" });
      });
    });
  }

  /* ----------------------------------------------------------- reveal --- */
  function initReveal(root) {
    var items = root.querySelectorAll(".reveal");
    var observer = getRevealObserver();
    if (prefersReducedMotion || !observer) {
      items.forEach(function (item) {
        item.classList.add("is-visible");
      });
      return;
    }
    items.forEach(function (item) {
      if (item.dataset.prizmInit) return;
      item.dataset.prizmInit = "1";
      observer.observe(item);
    });
  }

  /* --------------------------------------------------------------- init --- */
  function init(root) {
    if (!root || !root.querySelectorAll) return;
    initHoldCompare(root);
    initVideoToggle(root);
    initScrollControls(root);
    initReveal(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-inicializar cuando una sección se recarga.
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
